import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Calendar, Users, Scissors, LogOut,
  TrendingUp, UserCheck, CalendarCheck, Menu, X, Tag, Settings,
  ChevronLeft, ChevronRight, DollarSign
} from 'lucide-react';
import AdminBookings from './AdminBookings';
import AdminClients from './AdminClients';
import AdminStylists from './AdminStylists';
import AdminServices from './AdminServices';
import AdminSettings from './AdminSettings';
import AdminEarnings from './AdminEarnings';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function StatCard({ icon: Icon, label, value, color, header }) {
  return (
    <div className="stat-card" style={{ borderTop: `4px solid ${color}` }}>
      <div className="stat-card-header" style={{ backgroundColor: `${color}15`, color: color }}>
        {header}
      </div>
      <div className="stat-card-content">
        <div className="stat-card-icon" style={{ backgroundColor: color }}>
          <Icon size={24} strokeWidth={1.5} color="white" />
        </div>
        <div className="stat-card-info">
          <span className="stat-card-value">{value ?? '—'}</span>
          <span className="stat-card-label">{label}</span>
        </div>
      </div>
    </div>
  );
}

function DashboardHome() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    const pw = sessionStorage.getItem('admin_password');
    axios.get('/api/admin/stats', { headers: { 'x-admin-password': pw } })
      .then(r => setStats(r.data)).catch(() => {});
    
    // Fetch bookings
    axios.get('/api/bookings')
      .then(r => setBookings(r.data)).catch(() => {});
    
    // Fetch stylists
    axios.get('/api/stylists')
      .then(r => setStylists(r.data)).catch(() => {});
  }, []);

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    // Empty cells for days before month starts
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDate = (day) => {
    if (!day) return [];
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(b => b.booking_date === dateStr && b.status !== 'cancelled');
  };

  const getStylistPhoto = (stylistId) => {
    const stylist = stylists.find(s => s.id === stylistId);
    if (!stylist?.photo) return null;
    // Adiciona /uploads/ antes do nome do arquivo
    const photoPath = stylist.photo.startsWith('/') ? stylist.photo : `/uploads/${stylist.photo}`;
    return photoPath.startsWith('http') ? photoPath : `${API_URL}${photoPath}`;
  };

  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const calendarDays = getDaysInMonth(currentMonth);

  return (
    <div className="admin-section">
      <h2 className="admin-section-title">Visão Geral</h2>
      <div className="stats-grid">
        <StatCard 
          icon={CalendarCheck} 
          label="Agendamentos Hoje" 
          value={stats?.todayBookings} 
          color="#3498db"
          header="Hoje"
        />
        <StatCard 
          icon={Calendar} 
          label="Próximos Agendamentos" 
          value={stats?.upcomingBookings} 
          color="#9b59b6"
          header="Próximos"
        />
        <StatCard 
          icon={UserCheck} 
          label="Clientes Únicos" 
          value={stats?.totalClients} 
          color="#27ae60"
          header="Clientes"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Total de Reservas" 
          value={stats?.totalBookings} 
          color="#e74c3c"
          header="Total"
        />
        <StatCard 
          icon={Scissors} 
          label="Profissionais" 
          value={stats?.totalStylists} 
          color="#f39c12"
          header="Equipe"
        />
      </div>

      {/* Calendar Section */}
      <div className="dashboard-calendar-section">
        <h3 className="calendar-title">
          <Calendar size={20} />
          Calendário de Agendamentos
        </h3>
        
        <div className="calendar-container">
          <div className="calendar-header">
            <button className="calendar-nav" onClick={prevMonth}>
              <ChevronLeft size={20} />
            </button>
            <span className="calendar-month">{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
            <button className="calendar-nav" onClick={nextMonth}>
              <ChevronRight size={20} />
            </button>
          </div>
          
          <div className="calendar-weekdays">
            {weekDays.map(day => (
              <div key={day} className="calendar-weekday">{day}</div>
            ))}
          </div>
          
          <div className="calendar-days">
            {calendarDays.map((day, index) => {
              const dayBookings = getBookingsForDate(day);
              const hasBookings = dayBookings.length > 0;
              const isToday = day === new Date().getDate() && 
                             currentMonth.getMonth() === new Date().getMonth() &&
                             currentMonth.getFullYear() === new Date().getFullYear();
              
              return (
                <div 
                  key={index} 
                  className={`calendar-day ${!day ? 'empty' : ''} ${isToday ? 'today' : ''} ${hasBookings ? 'has-bookings' : ''} ${selectedDate === day ? 'selected' : ''}`}
                  onClick={() => day && setSelectedDate(day === selectedDate ? null : day)}
                >
                  {day && (
                    <>
                      <span className="day-number">{day}</span>
                      {hasBookings && (
                        <div className="day-bookings">
                          <div className="booking-count">{dayBookings.length}</div>
                          <div className="booking-stylists">
                            {dayBookings.slice(0, 3).map((booking, idx) => (
                              <img 
                                key={idx}
                                src={getStylistPhoto(booking.stylist_id) || '/default-avatar.png'}
                                alt=""
                                className="stylist-thumb"
                                onError={(e) => e.target.src = '/default-avatar.png'}
                              />
                            ))}
                            {dayBookings.length > 3 && (
                              <span className="more-bookings">+{dayBookings.length - 3}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Selected Date Details */}
        {selectedDate && (
          <div className="selected-date-details">
            <h4>
              Agendamentos do dia {selectedDate}/{currentMonth.getMonth() + 1}/{currentMonth.getFullYear()}
            </h4>
            <div className="day-bookings-list">
              {getBookingsForDate(selectedDate).map((booking, idx) => (
                <div key={idx} className="booking-item">
                  <img 
                    src={getStylistPhoto(booking.stylist_id) || '/default-avatar.png'}
                    alt=""
                    className="booking-stylist-photo"
                    onError={(e) => e.target.src = '/default-avatar.png'}
                  />
                  <div className="booking-info">
                    <strong>{booking.client_name}</strong>
                    <span>{booking.booking_time} - {booking.service_name}</span>
                    <span className="booking-stylist">
                      {booking.stylist_name || 'Profissional'}
                    </span>
                  </div>
                  <span className={`booking-status ${booking.status}`}>
                    {booking.status === 'confirmed' ? 'Confirmado' : 'Pendente'}
                  </span>
                </div>
              ))}
              {getBookingsForDate(selectedDate).length === 0 && (
                <p className="no-bookings">Nenhum agendamento para este dia.</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="quick-links">
        <h3>Acesso Rápido</h3>
        <div className="quick-links-grid">
          <NavLink to="/admin/dashboard/agendamentos" className="quick-link-card">
            <Calendar size={28} strokeWidth={1.2} />
            <span>Ver Agendamentos</span>
          </NavLink>
          <NavLink to="/admin/dashboard/clientes" className="quick-link-card">
            <Users size={28} strokeWidth={1.2} />
            <span>Ver Clientes</span>
          </NavLink>
          <NavLink to="/admin/dashboard/profissionais" className="quick-link-card">
            <Scissors size={28} strokeWidth={1.2} />
            <span>Gerenciar Profissionais</span>
          </NavLink>
          <NavLink to="/admin/dashboard/servicos" className="quick-link-card">
            <Tag size={28} strokeWidth={1.2} />
            <span>Gerenciar Serviços</span>
          </NavLink>
          <NavLink to="/admin/dashboard/faturamento" className="quick-link-card">
            <DollarSign size={28} strokeWidth={1.2} />
            <span>Faturamento do Mês</span>
          </NavLink>
          <NavLink to="/admin/dashboard/configuracoes" className="quick-link-card">
            <Settings size={28} strokeWidth={1.2} />
            <span>Configurações</span>
          </NavLink>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const pw = sessionStorage.getItem('admin_password');
    if (!pw) navigate('/admin');
  }, [navigate]);

  const logout = () => {
    sessionStorage.removeItem('admin_password');
    navigate('/admin');
  };

  const navItems = [
    { to: '/admin/dashboard', end: true, icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/dashboard/agendamentos', icon: Calendar, label: 'Agendamentos' },
    { to: '/admin/dashboard/clientes', icon: Users, label: 'Clientes' },
    { to: '/admin/dashboard/profissionais', icon: Scissors, label: 'Profissionais' },
    { to: '/admin/dashboard/servicos', icon: Tag, label: 'Serviços' },
    { to: '/admin/dashboard/faturamento', icon: DollarSign, label: 'Faturamento' },
    { to: '/admin/dashboard/configuracoes', icon: Settings, label: 'Configurações' },
  ];

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Scissors size={18} strokeWidth={1.5} />
            <span>Patricia Pourroy</span>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon size={18} strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <button className="sidebar-logout" onClick={logout}>
          <LogOut size={16} strokeWidth={1.5} />
          <span>Sair</span>
        </button>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Main content */}
      <main className="admin-main">
        <header className="admin-topbar">
          <button className="topbar-menu" onClick={() => setSidebarOpen(true)}>
            <Menu size={22} />
          </button>
          <span className="topbar-title">Área Administrativa</span>
          <button className="topbar-logout" onClick={logout}>
            <LogOut size={16} /> Sair
          </button>
        </header>

        <div className="admin-content">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="agendamentos" element={<AdminBookings />} />
            <Route path="clientes" element={<AdminClients />} />
            <Route path="profissionais" element={<AdminStylists />} />
            <Route path="servicos" element={<AdminServices />} />
            <Route path="faturamento" element={<AdminEarnings />} />
            <Route path="configuracoes" element={<AdminSettings />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
